import React, { useState, forwardRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const sizes = {
  xs: {
    inputPadding: "4px 8px",
    fontSize: tokens.typography.fontSize["md-12px"],
    lineHeight: "16px",
    borderRadius: "6px",
    minHeight: "28px",
    buttonMinSize: "20px",
    buttonFontSize: "12px",
    buttonPadding: "0 4px",
    containerMargin: "2px",
  },
  sm: {
    inputPadding: "4px 10px",
    fontSize: tokens.typography.fontSize["md-12px"],
    lineHeight: "16px",
    borderRadius: "6px",
    minHeight: "32px",
    buttonMinSize: "24px",
    buttonFontSize: "14px",
    buttonPadding: "0 6px",
    containerMargin: "2px",
  },
  md: {
    inputPadding: "6px 10px",
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
    borderRadius: "7px",
    minHeight: "36px",
    buttonMinSize: "28px",
    buttonFontSize: "16px",
    buttonPadding: "0 8px",
    containerMargin: "4px",
  },
  lg: {
    inputPadding: "8px 10px",
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
    borderRadius: "7px",
    minHeight: "40px",
    buttonMinSize: "32px",
    buttonFontSize: "18px",
    buttonPadding: "0 10px",
    containerMargin: "4px",
  },
};

const defaultFocusVisibleState = {
  outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
  boxShadow: "0px 0px 0px 1px rgba(163, 163, 163, 0.50)",
};

const errorState = {
  outline: `1px solid ${tokens.colors.ui.destructive}`,
  boxShadow: "0px 0px 1px 1px rgba(196, 61, 81, 0.20)",
};

const StyledContainer = styled.div(
  ({ $size = "lg", $layout = "horizontal", $disabled = false }) => {
    const {
      inputPadding,
      fontSize,
      lineHeight,
      borderRadius,
      minHeight,
      containerMargin,
    } = sizes[$size];

    return {
      display: "inline-flex",
      alignItems: "center",
      width: "100%",
      borderRadius,
      overflow: "hidden",
      boxShadow: "0px 0px 0px 1px rgba(163, 163, 163, 0.50)",
      transition: "all 0.2s ease",
      fontFamily: tokens.typography.fontFamilies["albert-sans"],
      fontWeight: tokens.typography.fontWeights.regular,
      fontSize,
      lineHeight,
      padding: 0,
      minHeight,
      background: tokens.colors.ui.background,
      border: `1px solid ${tokens.colors.ui.border}`,
      color: tokens.colors.ui.foreground,

      "&:hover:not(.disabled)": {
        borderColor: tokens.colors.brand.blue["500"],
      },

      "&:focus-within:not(.disabled)": defaultFocusVisibleState,

      '&[data-error="true"]': errorState,

      "&:disabled, &.disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
        background: tokens.colors.brand.blue["100"],
      },

      "&:focus-within": {
        outline: "none",
      },

      ...($disabled && {
        opacity: 0.5,
        cursor: "not-allowed",
        background: tokens.colors.brand.blue["100"],
      }),
    };
  }
);

const StyledNumberInput = styled.input(({ $size = "lg" }) => {
  const { inputPadding, fontSize, lineHeight } = sizes[$size];

  return {
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    lineHeight: "inherit",
    color: tokens.colors.ui.foreground,
    minWidth: 0,
    padding: inputPadding,

    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },

    MozAppearance: "textfield",

    "&:disabled": {
      cursor: "not-allowed",
    },
  };
});

const StyledButtonGroup = styled.div(({ $size = "lg", $layout = "horizontal" }) => {
  const { containerMargin, borderRadius } = sizes[$size];

  return {
    display: "flex",
    flexDirection: $layout === "vertical" ? "column" : "row",
    alignItems: "stretch",
    gap: "1px", // Set directly here instead of in sizes
    background: tokens.colors.ui.border,
    margin: containerMargin,
    borderRadius: `calc(${borderRadius} - 2px)`,
    overflow: "hidden",
  };
});

const StyledStepButton = styled.button(({ $size = "lg" }) => {
  const { buttonFontSize, buttonPadding, buttonMinSize } = sizes[$size];

  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: tokens.colors.brand.blue["100"],
    border: "none",
    cursor: "pointer",
    transition: "background 0.2s",
    color: tokens.colors.ui.foreground,
    fontSize: buttonFontSize,
    fontWeight: tokens.typography.fontWeights.medium,
    padding: buttonPadding,
    minWidth: buttonMinSize,
    minHeight: buttonMinSize,

    "&:hover:not(:disabled)": {
      background: tokens.colors.brand.blue["200"],
    },

    "&:active:not(:disabled)": {
      background: tokens.colors.brand.blue["300"],
    },

    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },

    "&:focus-visible": {
      outline: `2px solid ${tokens.colors.ui.ring}`,
      outlineOffset: "-2px",
    },
  };
});

const StyledFieldWrapper = styled.div(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  width: "100%",
}));

const StyledLabel = styled.label(() => ({
  fontSize: tokens.typography.fontSize["lg-14px"],
  fontFamily: tokens.typography.fontFamilies["albert-sans"],
  fontWeight: tokens.typography.fontWeights.medium,
  lineHeight: "20px",
  color: tokens.colors.ui.foreground,
  display: "flex",
  alignItems: "center",
  gap: "4px",

  '&[data-required="true"]::after': {
    content: '"*"',
    color: tokens.colors.ui.destructive,
    marginLeft: "2px",
  },
}));

const StyledErrorText = styled.span(() => ({
  fontSize: tokens.typography.fontSize["md-12px"],
  fontFamily: tokens.typography.fontFamilies["albert-sans"],
  fontWeight: tokens.typography.fontWeights.medium,
  lineHeight: "16px",
  color: tokens.colors.ui.destructive,
  minHeight: "1.4em",
}));

const StyledHelperText = styled.span(() => ({
  fontSize: tokens.typography.fontSize["md-12px"],
  fontFamily: tokens.typography.fontFamilies["albert-sans"],
  fontWeight: tokens.typography.fontWeights.regular,
  lineHeight: "16px",
  color: tokens.colors.ui["muted-foreground"],
}));

/**
 * A number input component with increment/decrement buttons.
 *
 * @component
 * @param {Object} props
 * @param {number|string} [props.value] - Controlled value of the input
 * @param {function} [props.onChange] - Callback fired when value changes
 * @param {string} [props.size="lg"] - Size variant: "xs", "sm", "md", or "lg"
 * @param {string} [props.layout="horizontal"] - Button layout: "horizontal" or "vertical"
 * @param {number} [props.min] - Minimum allowed value
 * @param {number} [props.max] - Maximum allowed value
 * @param {number} [props.step=1] - Step increment for buttons
 * @param {boolean} [props.disabled=false] - If true, the input is disabled
 * @param {boolean} [props.error=false] - If true, applies error styling
 * @param {string} [props.placeholder="0"] - Placeholder text
 * @param {string} [props.label] - Label text
 * @param {string} [props.helperText] - Helper text to display
 * @param {string} [props.errorText] - Error message to display
 * @param {React.Ref} ref - Forwarded ref to the underlying input element
 * @returns {JSX.Element} The rendered number input component
 */
const NumberInput = forwardRef(
  (
    {
      value,
      onChange,
      size = "lg",
      layout = "horizontal",
      min,
      max,
      step = 1,
      disabled = false,
      error = false,
      placeholder = "0",
      label,
      helperText,
      errorText,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState(value ?? 0);
    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;

    const handleChange = (e) => {
      const val = e.target.value;

      if (val === "") {
        const newVal = 0;

        if (!isControlled) {
          setInternalValue(newVal);
        }

        if (onChange) {
          onChange(newVal);
        }

        return;
      }

      const num = parseFloat(val);

      if (!isNaN(num)) {
        if (!isControlled) {
          setInternalValue(num);
        }

        if (onChange) {
          onChange(num);
        }
      }
    };

    const handleStep = (direction) => {
      const num = parseFloat(currentValue) || 0;
      let newVal = num + direction * step;

      if (min !== undefined) {
        newVal = Math.max(newVal, min);
      }

      if (max !== undefined) {
        newVal = Math.min(newVal, max);
      }

      if (!isControlled) {
        setInternalValue(newVal);
      }

      if (onChange) {
        onChange(newVal);
      }
    };

    const handleBlur = (e) => {
      const num = parseFloat(e.target.value) || 0;
      let newVal = num;

      if (min !== undefined) {
        newVal = Math.max(newVal, min);
      }

      if (max !== undefined) {
        newVal = Math.min(newVal, max);
      }

      if (!isControlled) {
        setInternalValue(newVal);
      }

      if (onChange) {
        onChange(newVal);
      }
    };

    const isMinusDisabled =
      disabled ||
      (min !== undefined && (parseFloat(currentValue) || 0) <= min);

    const isPlusDisabled =
      disabled ||
      (max !== undefined && (parseFloat(currentValue) || 0) >= max);

    const showError = errorText ? true : error;

    return (
      <StyledFieldWrapper>
        {label && (
          <StyledLabel
            data-required={props.required ? "true" : undefined}
            htmlFor={props.id}
          >
            {label}
          </StyledLabel>
        )}

        <StyledContainer
          $size={size}
          $layout={layout}
          $disabled={disabled}
          data-error={showError ? "true" : undefined}
        >
          <StyledNumberInput
            ref={ref}
            type="number"
            $size={size}
            value={currentValue}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={placeholder}
            step={step}
            min={min}
            max={max}
            {...props}
          />

          <StyledButtonGroup $size={size} $layout={layout}>
            <StyledStepButton
              type="button"
              $size={size}
              onClick={() => handleStep(-1)}
              disabled={isMinusDisabled}
              aria-label="Decrease"
            >
              −
            </StyledStepButton>

            <StyledStepButton
              type="button"
              $size={size}
              onClick={() => handleStep(1)}
              disabled={isPlusDisabled}
              aria-label="Increase"
            >
              +
            </StyledStepButton>
          </StyledButtonGroup>
        </StyledContainer>

        {errorText ? (
          <StyledErrorText>{errorText}</StyledErrorText>
        ) : helperText ? (
          <StyledHelperText>{helperText}</StyledHelperText>
        ) : null}
      </StyledFieldWrapper>
    );
  }
);

NumberInput.displayName = "NumberInput";

export default NumberInput;
export {
  StyledFieldWrapper as FieldWrapper,
  StyledLabel as Label,
  StyledErrorText as ErrorText,
  StyledHelperText as HelperText,
};